import { ReactElement, useEffect, useState } from "react";
import {
  Box,
  ButtonGroup,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CheckBox,
  CheckBoxOutlineBlank,
  ExpandLess,
  ExpandMore,
  PushPin,
  PushPinOutlined,
  Search,
  ToggleOff,
} from "@mui/icons-material";
import React from "react";
import { useNavigate } from "react-router";

export interface MinibarItem {
  text: string;
  onClick?: () => void | Promise<void> | null;
  nav?: string;
  disabled?: boolean;
  invisible?: boolean;
}

export interface MinibarItemWithIcon extends MinibarItem {
  icon: React.ReactNode;
}

export interface MinibarItemWithComponent extends MinibarItem {
  component: JSX.Element;
  isCheckBox?: boolean;
  checked?: boolean;
}

export default function Minibar({
  minibarItems,
  barLength,
  large,
  searchLabel,
  onSearch,
  searchValue,
}: {
  minibarItems: (MinibarItemWithIcon | MinibarItemWithComponent)[];
  barLength: number;
  large?: boolean;
  searchLabel?: string;
  onSearch?: (value: string) => void;
  searchValue?: string;
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");
  const searchTextTimer = React.useRef<any>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (searchValue !== undefined) {
      setSearchText(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (searchTextTimer.current) {
      clearTimeout(searchTextTimer.current);
    }
    searchTextTimer.current = setTimeout(() => {
      if (onSearch) {
        onSearch(searchText);
      }
    }, 700);
  }, [searchText]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      (event.target as HTMLElement).closest("#expandButton")?.id ===
      "expandButton"
    ) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  async function handleItemClick(item: MinibarItem) {
    try {
      if (item.onClick) {
        console.log(`Minibar item clicked: ${item.text}`);
        await item.onClick(); // Await the async function
      }

      if (item.nav) {
        console.log(`Navigating to: ${item.nav}`);
        navigate(item.nav);
      }
    } catch (error) {
      console.error(`Error executing onClick for ${item.text}:`, error);
      // Optionally, you can display an error message to the user here
    } finally {
      if ((item as MinibarItemWithIcon).icon) {
        handleClose();
      }
    }
  }

  function ButtonGroupIconButton(props: any) {
    const {
      disableElevation,
      fullWidth,
      variant,
      tooltip,
      ...iconButtonProps
    } = props;
    return (
      <Tooltip title={tooltip} placement="top" disableInteractive>
        <span>
          <IconButton {...iconButtonProps} />
        </span>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      {onSearch && (
        <TextField
          fullWidth
          value={searchText}
          placeholder={searchLabel || "Search"}
          label={searchLabel || "Search"}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "1px solid rgba(224,224,224,1)!important",
              },
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "#E0E0E0" }} />
                </InputAdornment>
              ),
              sx: {
                height: large ? "43px" : "32px",
                backgroundColor: "#212121",
                border: "none",
                borderRadius: "3px",
                marginRight: "18px",
                padding: "0px 9px",
              },
            },
            inputLabel: { style: { color: "#E0E0E0" } },
          }}
        />
      )}
      {minibarItems.length !== 0 && (
        <>
          <ButtonGroup
            variant={"outlined"}
            aria-label="primary button group"
            size="small"
            onClick={handleClick}
            sx={{ pl: 1 }}
          >
            {barLength > 0 &&
              minibarItems
                .filter(
                  (item) =>
                    !item.invisible &&
                    (Boolean((item as MinibarItemWithIcon).icon) ||
                      Boolean((item as MinibarItemWithComponent).isCheckBox))
                )
                .map(
                  (item, index) =>
                    index < barLength && (
                      <ButtonGroupIconButton
                        tooltip={item.text}
                        key={index}
                        onClick={async () => await handleItemClick(item)}
                        disabled={item.disabled}
                        sx={
                          index === 0
                            ? {
                                borderTopLeftRadius: "10%",
                                borderBottomLeftRadius: "10%",
                                borderTopRightRadius: "0%",
                                borderBottomRightRadius: "0%",
                                border: "1px solid #ECECEC",
                                minWidth: large ? "55px" : "45px",
                                maxHeight: large ? "43px" : "32px",
                                minHeight: large ? "43px" : "32px",
                                padding: "0px 3px",
                                marginRight: "-1px",
                                color: "#ECECEC",
                                backgroundColor: "#212121",
                              }
                            : {
                                borderRadius: "0%",
                                border: "1px solid rgba(224,224,224,1)",
                                minWidth: "40px",
                                maxHeight: large ? "43px" : "32px",
                                minHeight: large ? "43px" : "32px",
                                padding: "0px 3px",
                                marginRight: "-1px",
                                color: "#E0E0E0",
                                backgroundColor: "#212121",
                              }
                        }
                        size="small"
                      >
                        {Boolean((item as MinibarItemWithIcon).icon)
                          ? React.cloneElement(
                              (item as MinibarItemWithIcon)
                                .icon as ReactElement,
                              large ? { fontSize: "large" } : {}
                            )
                          : (item as MinibarItemWithComponent).checked
                          ? React.cloneElement(
                              (
                                <CheckBox htmlColor="lightblue" />
                              ) as ReactElement,
                              large ? { fontSize: "large" } : {}
                            )
                          : React.cloneElement(
                              (<CheckBoxOutlineBlank />) as ReactElement,
                              large ? { fontSize: "large" } : {}
                            )}
                      </ButtonGroupIconButton>
                    )
                )}

            <ButtonGroupIconButton
              tooltip={open ? "" : "More Options"}
              id="expandButton"
              size="small"
              style={
                barLength === 0
                  ? {
                      border: "1px solid rgba(224,224,224,1)",
                      borderRadius: "10%",
                      maxHeight: "32px",
                      color: "#E0E0E0",
                      backgroundColor: "#212121",
                    }
                  : {
                      border: "1px solid rgba(224,224,224,1)",
                      maxWidth: large ? "32px" : "24px",
                      minWidth: large ? "32px" : "24px",
                      borderTopRightRadius: "10%",
                      borderBottomRightRadius: "10%",
                      borderTopLeftRadius: "0%",
                      borderBottomLeftRadius: "0%",
                      maxHeight: large ? "43px" : "32px",
                      minHeight: large ? "43px" : "32px",
                      color: "#E0E0E0",
                      backgroundColor: "#212121",
                    }
              }
            >
              {open ? (
                <ExpandLess fontSize={large ? "large" : "medium"} />
              ) : (
                <ExpandMore fontSize={large ? "large" : "medium"} />
              )}
            </ButtonGroupIconButton>
          </ButtonGroup>

          <Menu
            elevation={0}
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "black",
                color: "white",
                border: "1px solid rgba(224,224,224,1)",
              },
            }}
          >
            {minibarItems.map(
              (item, index) =>
                !item.invisible && (
                  <MenuItem
                    key={index}
                    onClick={async () => await handleItemClick(item)}
                    disabled={item.disabled}
                    sx={{
                      color: "white",
                      backgroundColor: "black",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        color: "white",
                      },
                    }}
                  >
                    {(item as MinibarItemWithIcon).icon ? (
                      <>
                        {(item as MinibarItemWithIcon).icon}
                        <span style={{ width: "7px" }}></span>
                        {item.text}
                      </>
                    ) : (
                      <>{(item as MinibarItemWithComponent).component}</>
                    )}
                  </MenuItem>
                )
            )}
          </Menu>
        </>
      )}
    </Box>
  );
}

export interface AccordionToolbarItem {
  tooltip: string;
  icon: React.ReactNode;
  onClick: () => void | Promise<void>;
}

export function RenderAccordionToolbar({
  accordionToolbarItems = [],
  pinned,
  panel,
  togglePin,
}: {
  accordionToolbarItems?: AccordionToolbarItem[];
  pinned: Set<string>;
  panel: string;
  togglePin: (panel: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        marginTop: "-8px",
      }}
    >
      {accordionToolbarItems.map((item, index) => (
        <Tooltip
          title={item.tooltip}
          key={index}
          placement="top"
          disableInteractive
        >
          <span>
            <IconButton
              onClick={async () => await item.onClick()}
              size="small"
              sx={{
                color: "#E0E0E0",
              }}
            >
              {React.cloneElement(item.icon as ReactElement, {
                fontSize: "small",
              })}
            </IconButton>
          </span>
        </Tooltip>
      ))}
      <Tooltip title={"Pin Open"} placement="top" disableInteractive>
        <span>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              togglePin(panel);
            }}
            size="small"
            sx={{
              color: "#E0E0E0",
            }}
          >
            {React.cloneElement(
              (pinned.has(panel) ? (
                <PushPin />
              ) : (
                <PushPinOutlined />
              )) as ReactElement,
              {
                fontSize: "small",
              }
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
